import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { apiGet } from "@/lib/apiClient";
import { crmContactDisplayName } from "@/lib/crmContactParty";
import { formatCrmStatusLabel } from "@/lib/crmDisplay";
import {
  CreateInvoiceFromDealButton,
  CrmDealEditSection,
  type DealEditSnapshot,
} from "@/components/crm/CrmDealEditSection";
import { CrmRecordDetailLayout } from "@/components/crm/CrmRecordDetailLayout";
import { resolveCrmRecordTab } from "@/components/crm/CrmRecordTabs";
import { RecordTimeline, type RecordTimelineEvent } from "@/components/crm/RecordTimeline";
import { ConnectedRecordsPanel } from "@/components/crm/ConnectedRecordsPanel";

type AccountRow = { id: string; name?: string | null; domain?: string | null };
type ContactRow = {
  id: string;
  fullName?: string | null;
  email?: string | null;
  title?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};
type DealRow = { id: string; name?: string | null; stage?: string | null };
type LeadRow = { id: string; leadName?: string; stage?: string; status?: string; accountId?: string | null };

export function CrmDealDetailPage() {
  const { dealId: idParam } = useParams<{ dealId: string }>();
  const dealId = idParam ? decodeURIComponent(idParam) : "";
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("accountId") || "";
  const activeTab = resolveCrmRecordTab(searchParams.get("tab"));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deal, setDeal] = useState<DealEditSnapshot | null>(null);
  const [account, setAccount] = useState<AccountRow | null>(null);
  const [accountContacts, setAccountContacts] = useState<ContactRow[]>([]);
  const [accountDeals, setAccountDeals] = useState<DealRow[]>([]);
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [timeline, setTimeline] = useState<RecordTimelineEvent[]>([]);

  const tabSearch = useMemo(() => {
    const p = new URLSearchParams(searchParams);
    p.delete("tab");
    return p;
  }, [searchParams]);

  useEffect(() => {
    if (!dealId || !accountId) {
      setLoading(false);
      if (!accountId) setError("Open this deal from the deals list (accountId query required).");
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const [dealRes, accountRes, contactsRes, dealsRes, leadsRes] = await Promise.all([
        apiGet<DealEditSnapshot>(
          `/api/crm/deals/${encodeURIComponent(dealId)}?accountId=${encodeURIComponent(accountId)}`,
        ),
        apiGet<AccountRow>(`/api/crm/accounts/${encodeURIComponent(accountId)}`),
        apiGet<{ contacts?: ContactRow[] }>(`/api/crm/contacts?accountId=${encodeURIComponent(accountId)}`),
        apiGet<{ deals?: DealRow[] }>(`/api/crm/deals?accountId=${encodeURIComponent(accountId)}`),
        apiGet<{ leads?: LeadRow[] }>("/api/crm/leads?limit=200"),
      ]);
      if (cancelled) return;
      if (!dealRes.ok || !dealRes.data) {
        setError(dealRes.error || "Deal not found.");
        setDeal(null);
      } else {
        setDeal({ ...dealRes.data, id: dealId });
        setError(null);
      }
      setAccount(accountRes.ok && accountRes.data ? accountRes.data : null);
      setAccountContacts(contactsRes.ok ? (contactsRes.data?.contacts ?? []) : []);
      setAccountDeals(dealsRes.ok ? (dealsRes.data?.deals ?? []) : []);
      const allLeads = leadsRes.ok ? (leadsRes.data?.leads ?? []) : [];
      setLeads(allLeads.filter((l) => l.accountId === accountId));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId, accountId]);

  useEffect(() => {
    if (!dealId || activeTab !== "history") {
      setTimeline([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await apiGet<{ events?: RecordTimelineEvent[] }>(
        `/api/audit/app/crm/module/deals/record/${encodeURIComponent(dealId)}?limit=120`,
      );
      if (cancelled) return;
      setTimeline(res.ok ? (res.data?.events ?? []) : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId, activeTab]);

  if (loading) return <p className="text-sm text-slate-500">Loading deal…</p>;
  if (error && !deal) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
        <Link to="/crm/deals" className="text-sm text-indigo-600 hover:underline">
          All deals
        </Link>
      </div>
    );
  }
  if (!deal || !accountId) return null;

  const basePath = `/crm/deals/${encodeURIComponent(dealId)}`;
  const accountLabel = account?.name?.trim() || accountId;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500">Deal detail</p>
          <h1 className="text-2xl font-semibold">{deal.name || dealId}</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            {formatCrmStatusLabel(deal.status)} · Stage {formatCrmStatusLabel(deal.stage)} ·{" "}
            <Link to={`/crm/accounts/${encodeURIComponent(accountId)}`} className="text-indigo-600 hover:underline">
              View account
            </Link>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CreateInvoiceFromDealButton dealId={dealId} accountId={accountId} dealStage={deal.stage} />
          <Link to="/crm/deals" className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50 dark:border-white/20">
            Back
          </Link>
        </div>
      </div>

      <CrmRecordDetailLayout
        basePath={basePath}
        activeTab={activeTab}
        searchParams={tabSearch}
        connectedRecords={
          <ConnectedRecordsPanel
            compact
            selectedRecordId={dealId}
            accounts={[
              {
                id: accountId,
                label: accountLabel,
                href: `/crm/accounts/${encodeURIComponent(accountId)}`,
                meta: account?.domain || accountId,
                icon: "building",
              },
            ]}
            contacts={accountContacts.map((c) => ({
              id: c.id,
              label: crmContactDisplayName(c),
              href: `/crm/contacts/${encodeURIComponent(c.id)}?accountId=${encodeURIComponent(accountId)}`,
              meta: c.title || c.email || "No email",
              icon: "user",
            }))}
            deals={accountDeals.map((d) => ({
              id: d.id,
              label: d.name || d.id,
              href: `/crm/deals/${encodeURIComponent(d.id)}?accountId=${encodeURIComponent(accountId)}`,
              meta: formatCrmStatusLabel(d.stage),
            }))}
            leads={leads.map((l) => ({
              id: l.id,
              label: l.leadName || l.id,
              href: `/crm/leads/${encodeURIComponent(l.id)}`,
              meta: `${formatCrmStatusLabel(l.stage)} · ${formatCrmStatusLabel(l.status)}`,
            }))}
            createContactHref={`/crm/contacts/new?accountId=${encodeURIComponent(accountId)}`}
            createDealHref={`/crm/deals/new?accountId=${encodeURIComponent(accountId)}`}
            hideEmptyLeads
          />
        }
      >
        {activeTab === "overview" ? (
          <div className="space-y-4">
            <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-white/5">
              <h2 className="text-lg font-semibold">Deal overview</h2>
              <dl className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                <div>
                  <dt className="text-gray-500">Amount</dt>
                  <dd className="font-medium">{deal.amount != null ? deal.amount.toLocaleString() : "—"}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Close date</dt>
                  <dd className="font-medium">
                    {deal.closeDate ? new Date(deal.closeDate).toLocaleDateString() : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Owner</dt>
                  <dd className="font-medium">{deal.ownerId || "Unassigned"}</dd>
                </div>
              </dl>
            </section>
            <CrmDealEditSection
              dealId={dealId}
              accountId={accountId}
              initial={deal}
              accountContacts={accountContacts}
              onSaved={(updated) => setDeal((prev) => (prev ? { ...prev, ...updated } : prev))}
            />
          </div>
        ) : (
          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">History</h2>
              <Link
                to={`/crm/audit/deals/${encodeURIComponent(dealId)}`}
                className="text-sm text-indigo-600 hover:underline dark:text-indigo-300"
              >
                Open full audit page
              </Link>
            </div>
            <RecordTimeline events={timeline} emptyMessage="No record history for this deal yet." />
          </section>
        )}
      </CrmRecordDetailLayout>
    </div>
  );
}
